/**
 * Design tokens — single source of truth for all style values.
 *
 * Rules:
 * - All colours, typography, spacing, radii, shadows must come from here.
 * - Styled components import from this file — never hardcode values inline.
 * - Keep in sync with theme.ts (breakpoints, palette).
 */

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

export const colors = {
	// Brand
	brand: '#090304',
	brandHover: 'rgba(9,3,4,0.9)',

	// Text (opacity-based — matches MUI palette.text)
	textPrimary: 'rgba(0,0,0,0.87)',
	textSecondary: 'rgba(0,0,0,0.6)',
	textDisabled: 'rgba(0,0,0,0.38)',
	textDisabledOnDark: 'rgba(255,255,255,0.38)',
	textInverse: '#ffffff',
	textAddress: '#475467', // muted address / subtitle text

	// Surfaces
	surfaceWhite: '#ffffff',
	surfaceMuted: '#fafafa',
	// Frosted-glass surface — compact/scrolled header background
	surfaceMutedFrosted: 'rgba(250,250,250,0.95)',

	// Overlays / borders
	border: 'rgba(0,0,0,0.16)',
	borderSubtle: 'rgba(0,0,0,0.12)',
	borderTransparent: 'transparent', // zero-width border placeholder — preserves layout on focus transitions
	divider: 'rgba(0,0,0,0.12)',
	focusBorder: 'rgba(0,0,0,0.3)',
	overlayHeavy: 'rgba(0,0,0,0.54)',
	overlayMedium: 'rgba(0,0,0,0.08)',
	overlayLight: 'rgba(0,0,0,0.06)',
	overlaySubtle: 'rgba(0,0,0,0.04)',

	// Icon
	iconActive: 'rgba(0,0,0,0.56)',

	// Semantic
	// error / warning — MUI palette values, used for system-level states (form errors, alerts)
	error: '#D32F2F',
	errorAlt: '#B00020', // Figma design uses this for StoreClosed component
	errorAltLight: 'rgba(176,0,32,0.08)',
	errorBg: 'rgba(211,47,47,0.08)',
	errorBorder: 'rgba(211,47,47,0.32)',
	warning: '#ED6C02',
	// errorFigma / warningFigma — Figma design-system values, used for inline UI labels
	// (SoldOutLabel, LowStockLabel) where the designer intentionally chose a different shade.
	// Do not use these interchangeably with error/warning.
	errorFigma: '#D92D20',
	warningFigma: '#F6A735',
	warningBg: 'rgba(237,108,2,0.12)',
	warningLight: 'rgba(237,108,2,0.12)',
	warningPulseStart: 'rgba(246,167,53,0.18)',
	warningPulseEnd: 'rgba(246,167,53,0.2)',
	success: '#4CAF50',
	successLight: 'rgba(46,125,50,0.12)',
	errorLight: 'rgba(211,47,47,0.12)',

	// Overlays
	overlayBackdrop: 'rgba(0,0,0,0.25)',
	// Drag-pill handle (Figma-specified, between overlaySubtle and overlayLight)
	overlayHandle: 'rgba(0,0,0,0.15)',
} as const

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const typography = {
	// Font family
	fontFamily: '"Nunito Sans", sans-serif',

	// Font sizes
	fontSizeXs: 11,
	fontSizeSm: 12,
	fontSizeMd: 14,
	fontSizeLg: 16,
	fontSizeXl: 20,
	fontSizeIcon: 22,

	// Font weights
	fontWeightRegular: 400,
	fontWeightSemiBold: 600,
	fontWeightBold: 700,

	// Line heights
	lineHeightXs: '14px',
	lineHeightSm: '16px',
	lineHeightMd: '20px',
	lineHeightLg: '24px',
	lineHeightXl: '28px',

	// Letter spacing
	letterSpacingTight: '0.1px',
	letterSpacingNormal: '0.15px',
	letterSpacingNormalLg: '0.18px',
	letterSpacingWide: '0.25px',
	letterSpacingWidest: '0.4px',
} as const

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

export const spacing = {
	xxs: 2,
	xs: 4,
	sm: 8,
	md: 12,
	lg: 16,
	xl: 24,
	xxl: 32,
	// Desktop page-level horizontal padding and column gap (Figma 562:39005)
	xxxl: 48,
	// Page-level bottom padding — gives breathing room below last content section
	pageBottomPadding: 64,
	// Horizontal padding inside badge chips (Figma-specified, no standard step between xs and sm)
	badgePaddingX: 6,
} as const

// ---------------------------------------------------------------------------
// Border radius
// ---------------------------------------------------------------------------

export const radius = {
	badge: 4,
	card: 8,
	dropdown: 12,
	searchBar: 20,
	sheet: 16,
	pill: 100,
	circle: '50%',
} as const

// ---------------------------------------------------------------------------
// Shadows
// ---------------------------------------------------------------------------

export const shadows = {
	card: '0px 2px 5px rgba(0,0,0,0.03)',
	dropdown: '0px 4px 16px rgba(0,0,0,0.12)',
	modal: '0px 1px 3px rgba(0,0,0,0.2), 0px 2px 1px rgba(0,0,0,0.12), 0px 1px 1px rgba(0,0,0,0.14)',
	floatingButton: '0 1px 4px rgba(0,0,0,0.2)',
} as const

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

export const transitions = {
	fast: '0.15s',
	// Spatial animations — used for layout changes (grid-template-rows, padding, gap)
	spatial: '0.28s cubic-bezier(0.32, 0.72, 0, 1)',
	// Opacity animations — used for fade in/out
	opacity: '0.22s ease',
} as const

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

export const effects = {
	backdropBlur: 'blur(12px)',
} as const

// ---------------------------------------------------------------------------
// Z-index
// ---------------------------------------------------------------------------

export const zIndex = {
	stickyHeader: 10,
	storeClosedOverlay: 1300,
} as const

// ---------------------------------------------------------------------------
// Skeleton animation opacities — shared across all skeleton components
// ---------------------------------------------------------------------------

export const skeletonOpacity = {
	high: 0.92,
	mid: 0.72,
	reduced: 0.82,
} as const

// ---------------------------------------------------------------------------
// Component sizing
// ---------------------------------------------------------------------------

export const sizing = {
	// Interactive element heights
	// Both mobile and desktop pills are 32px per the current Figma spec.
	// WCAG 2.5.5 recommends 44px touch targets on mobile — this is an intentional
	// design decision; revisit if accessibility audits flag it.
	pillHeightMobile: 32,
	pillHeightDesktop: 32,
	iconButton: 40,
	addButton: 36,
	searchBarHeight: 32,
	searchBarWidth: 160,
	searchClearButton: 20,
	navIcon: 20,

	// Avatar
	avatarMobile: 48,
	avatarDesktop: 64,

	// Card dimensions
	cardHeightMobile: 128,
	cardHeightDesktop: 144,
	cardImageWidth: 144,

	// Layout
	cartSidebarWidth: 320, // lg (1025px) — compact desktop
	cartSidebarWidthWide: 400, // xl (1280px+) — wide desktop
	layoutMaxWidth: 1280,
	// Max content width at tablet (md) — Figma tablet frame is 1024px wide with content
	// constrained to 832px centred inside a full-width container with 16px side padding.
	tabletContentMaxWidth: 832,

	// Skeleton block heights — match typography line-height scale
	skeletonTextHeightMd: 20,
	skeletonTextHeightSm: 16,
	skeletonHeadingMobile: 24,
	skeletonHeadingDesktop: 28,
	skeletonPriceWidth: 48,
	// Page bottom padding
	pageBottomPadding: 64,

	// Skeleton pill placeholder widths (rem — proportional, not pixel-exact)
	skeletonPillWidths: ['4.5rem', '5.25rem', '3.75rem', '6rem'] as string[],
	// Skeleton category header widths
	skeletonCategoryHeaderWidths: ['7rem', '5.5rem', '8rem'] as string[],
	// Skeleton item title widths (percentage strings)
	skeletonItemTitleWidths: ['72%', '58%', '80%', '50%', '65%', '68%'] as string[],
	// Skeleton item description line widths
	skeletonItemDescWidths: ['82%', '60%'] as string[],

	// Layout min-height — svh excludes mobile browser chrome (avoids iOS overscroll)
	layoutMinHeight: '100svh',

	// Cart item thumbnail
	cartItemImage: 48,

	// Misc
	loaderSize: 16,
	hamburgerBadgeMinWidth: 16,
	moreMenuMinWidth: 160,
	emptySearchMinHeight: 294,
	clearIcon: 14,
	// Offset scroll-to-section so category headings land below the sticky header
	stickyHeaderScrollOffset: 180,
	// Mobile bottom sheet max height
	mobileSheetMaxHeight: '80vh',
	// Minimum card width — Figma spec for two-column layout
	menuItemMinWidth: 260,
	menuItemMaxWidthDesktop: 408,
	// Add button circle offset from card bottom-right corner
	addButtonOffset: 7,

	// Item modal measurements
	itemImageHeight: 240,
	badgeSize: 20, // required badge + low-stock badge height/width
	thumbSize: 44, // compact-header thumbnail
	specialRequestMinHeight: 48,

	// Drag pill handle (Figma-specified overlay indicator)
	dragPillWidth: 40,
	dragPillHeight: 4,

	// Modifier set row / action bar
	modifierRowMinHeight: 56,
	modifierBarMinHeight: 72,

	// Header — Figma-derived measurements for the 720px tablet breakpoint
	buttonHeight: 36,
	signInButtonMinWidth: 117,
	schedulePillMaxWidth: 269,
	brandingBoxMaxWidth: 491,
	// Icon-only sign-in button (compact breakpoint)
	signInIconButton: 36,
	// Avatar size in compact/scrolled header
	avatarCompact: 36,
} as const

// ---------------------------------------------------------------------------
// Typography presets — named combinations of size + weight + lineHeight + letterSpacing
// Use these instead of repeating the four properties individually.
// ---------------------------------------------------------------------------

export const typePresets = {
	// UI labels, buttons, pill text
	labelMd: {
		fontSize: typography.fontSizeMd,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightMd,
		letterSpacing: typography.letterSpacingWide,
	},
	// Larger labels — sheet items, cancel buttons, section CTAs
	labelLg: {
		fontSize: typography.fontSizeMd,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingWide,
	},
	// Body copy, inputs, descriptions
	bodyMd: {
		fontSize: typography.fontSizeMd,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightMd,
		letterSpacing: typography.letterSpacingWide,
	},
	// Section headings, sheet titles, category names
	headingLg: {
		fontSize: typography.fontSizeLg,
		fontWeight: typography.fontWeightBold,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingNormal,
	},
	// Captions, tags, secondary labels
	bodySm: {
		fontSize: typography.fontSizeSm,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightSm,
		letterSpacing: typography.letterSpacingWidest,
	},
} as const

// ---------------------------------------------------------------------------
// Focus styles — shared across all interactive elements
// ---------------------------------------------------------------------------

export const focusVisible = {
	outline: '2px solid',
	outlineOffset: 2,
} as const

// ---------------------------------------------------------------------------
// Breakpoints — sourced from Figma Online Ordering V2 design file
//   compact       ≤ 360px  (sign-in pill collapses to icon-only)
//   mobile        ≤ 720px  (tablet-mobile range)
//   desktopSm   721–1024px
//   desktopLg    ≥ 1025px
// ---------------------------------------------------------------------------

export const breakpoints = {
	compact: 360,
	mobile: 720,
	desktopSm: 721,
	desktopLg: 1025,
} as const
